import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { isNative } from '../utils/platform';
import { navigateTo } from '../services/navigation';
// The i18next singleton (initialised in i18n/config); a class component can't use the hook.
import i18n from 'i18next';

/**
 * Enterprise Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log error for debugging
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);

    // TODO: Send to error tracking service (e.g., Sentry)
    // if (process.env.NODE_ENV === 'production') {
    //   sendToErrorTracking(error, errorInfo);
    // }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    // Web: hard load to root clears the broken state. Native: no SPA-fallback
    // server, so route through react-router instead.
    if (isNative) {
      navigateTo('/', { replace: true });
    } else {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          retry: this.handleRetry
        });
      }

      // Default fallback UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: this.props.fullPage ? '100vh' : '400px',
          padding: '2rem',
          backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
          textAlign: 'center'
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface, #ffffff)',
            borderRadius: '1rem',
            padding: '2.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Error Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <AlertTriangle size={32} color="#EF4444" />
            </div>

            {/* Title */}
            <h2 style={{
              margin: '0 0 0.75rem',
              fontSize: '1.5rem',
              fontWeight: '600',
              color: 'var(--color-text-primary, #1f2937)'
            }}>
              {i18n.t('errorBoundary.title')}
            </h2>

            {/* Description */}
            <p style={{
              margin: '0 0 1.5rem',
              fontSize: '1rem',
              color: 'var(--color-text-secondary, #6b7280)',
              lineHeight: '1.6'
            }}>
              {i18n.t('errorBoundary.message')}
            </p>

            {/* Error details (development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginBottom: '1.5rem',
                textAlign: 'left',
                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                borderRadius: '0.5rem',
                padding: '1rem'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: '#EF4444',
                  fontSize: '0.875rem'
                }}>
                  {i18n.t('errorBoundary.details')}
                </summary>
                <pre style={{
                  marginTop: '0.75rem',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  color: 'var(--color-text-secondary, #6b7280)'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleRetry}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--color-primary, #2563eb)',
                  color: 'var(--color-on-primary, #fff)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-dark, #1d4ed8)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary, #2563eb)'}
              >
                <RefreshCw size={18} />
                {i18n.t('errorBoundary.tryAgain')}
              </button>

              <button
                onClick={this.handleGoHome}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-secondary, #6b7280)',
                  border: '1px solid var(--color-border, #e5e7eb)',
                  borderRadius: '0.5rem',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary, #f9fafb)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <Home size={18} />
                {i18n.t('errorBoundary.goHome')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap a component with error boundary
 */
export function withErrorBoundary(WrappedComponent, fallback = null) {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

/**
 * Page-level error boundary with full-page styling
 */
export function PageErrorBoundary({ children }) {
  return (
    <ErrorBoundary fullPage>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
